import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Clock, Users, Coffee, AlertTriangle, Download, FileText, RefreshCw, Edit, ShieldOff, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { formatPunchTime, getUtcOffsetLabel, getCurrentTimeInTz } from '../lib/timeFormat';

export default function Attendance() {
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [searchParams] = useSearchParams();
  const [attendances, setAttendances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState({ categories: [], positions: [] });
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(searchParams.get('employee_id') || '');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionData, setCorrectionData] = useState({
    employee_id: '', date: '', check_in: '', check_out: '', break_start: '', break_end: '', reason: ''
  });

  // ── Annulation retard / absence ──
  const [waiveOpen, setWaiveOpen]   = useState(false);
  const [waiveTarget, setWaiveTarget] = useState(null);   // enregistrement concerné
  const [waiveLate,   setWaiveLate]   = useState(false);
  const [waiveAbsent, setWaiveAbsent] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiving,     setWaiving]     = useState(false);

  // ── Annulation en masse (force majeure) ──
  const [bulkWaiveOpen,   setBulkWaiveOpen]   = useState(false);
  const [bulkStart,       setBulkStart]       = useState('');
  const [bulkEnd,         setBulkEnd]         = useState('');
  const [bulkWaiveLate,   setBulkWaiveLate]   = useState(true);
  const [bulkWaiveAbsent, setBulkWaiveAbsent] = useState(true);
  const [bulkReason,      setBulkReason]      = useState('');
  const [bulkWaiving,     setBulkWaiving]     = useState(false);

  // Default: first day of current month to today
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const empFromQuery = searchParams.get('employee_id');
  // If we arrive from Dashboard widget, broaden range to 30 days to show history
  const defaultStart = empFromQuery
    ? format(new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()), 'yyyy-MM-dd')
    : format(firstDay, 'yyyy-MM-dd');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(format(now, 'yyyy-MM-dd'));
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [deviceTimezones, setDeviceTimezones] = useState([]);

  useEffect(() => {
    fetchEmployees();
    fetchEmployeeOptions();
    fetchAttendances();
    fetchDeviceTimezones();
  }, []);

  const fetchDeviceTimezones = async () => {
    try {
      const res = await api.get('/zkteco/devices/status');
      const tzs = (res.data || [])
        .filter(d => d.is_active !== false)
        .map(d => ({
          serial: d.serial_number || d.device_id,
          name: d.device_name,
          location: d.location,
          timezone: d.timezone || 'Africa/Kinshasa'
        }));
      setDeviceTimezones(tzs);
    } catch (e) {
      console.error('Failed to fetch device timezones', e);
    }
  };

  // Real-time auto-refresh: poll /api/attendance every 30s when tab is visible.
  // Only fetches attendance (not employees) to stay light.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAttendancesSilent();
      }
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedEmployee, startDate, endDate, selectedCategory, selectedPosition]);

  // Tick every second just to update "il y a Xs" label
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const fetchAttendancesSilent = async () => {
    try {
      const params = {};
      if (selectedEmployee && selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedPosition && selectedPosition !== 'all') params.position = selectedPosition;
      const data = (await api.get('/attendance', { params })).data;
      setAttendances(data);
      setLastRefresh(new Date());
    } catch (e) {
      // silent: no toast on background polls
      console.warn('auto-refresh failed', e);
    }
  };

  const fetchEmployees = async () => {
    try { setEmployees((await api.get('/employees')).data); }
    catch (e) { toast.error('Erreur chargement employes'); }
  };

  const fetchEmployeeOptions = async () => {
    try { setEmployeeOptions((await api.get('/employee-options')).data); }
    catch (e) { console.error(e); }
  };

  const fetchAttendances = async () => {
    try {
      const params = {};
      if (selectedEmployee && selectedEmployee !== 'all') params.employee_id = selectedEmployee;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedCategory && selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedPosition && selectedPosition !== 'all') params.position = selectedPosition;
      setAttendances((await api.get('/attendance', { params })).data);
      setLastRefresh(new Date());
    } catch (e) { toast.error('Erreur chargement presences'); }
    finally { setLoading(false); }
  };

  const handleFilter = () => { setLoading(true); fetchAttendances(); };

  const handleExport = async (fmt) => {
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate, format: fmt });
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedPosition && selectedPosition !== 'all') params.append('position', selectedPosition);

      const response = await api.get(`/export/attendance?${params.toString()}`, { responseType: 'blob' });
      const ext = fmt === 'pdf' ? 'pdf' : 'xlsx';
      const mime = fmt === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presences_${startDate}_${endDate}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} telecharge`);
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const handleRecalculate = async (employeeId, date) => {
    try {
      await api.post(`/attendance/recalculate?employee_id=${employeeId}&date=${date}`);
      toast.success('Journee recalculee');
      fetchAttendances();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur recalcul'); }
  };

  const openCorrection = (att) => {
    const timeStr = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toTimeString().slice(0, 5); } catch { return ''; }
    };
    setCorrectionData({
      employee_id: att.employee_id, date: att.date,
      check_in: timeStr(att.check_in), check_out: timeStr(att.check_out),
      break_start: timeStr(att.break_start), break_end: timeStr(att.break_end), reason: ''
    });
    setCorrectionOpen(true);
  };

  const handleCorrection = async (e) => {
    e.preventDefault();
    try {
      await api.post('/attendance/manual-correction', correctionData);
      toast.success('Correction appliquee');
      setCorrectionOpen(false);
      fetchAttendances();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const openWaive = (att) => {
    setWaiveTarget(att);
    setWaiveLate(att.is_late || false);
    setWaiveAbsent(att.is_absent || false);
    setWaiveReason('');
    setWaiveOpen(true);
  };

  const handleWaive = async () => {
    if (!waiveLate && !waiveAbsent) { toast.error('Sélectionnez au moins un élément à annuler'); return; }
    if (!waiveReason.trim())        { toast.error('Le motif est obligatoire'); return; }
    setWaiving(true);
    try {
      await api.patch(`/attendance/${waiveTarget.id}/waive`, {
        waive_late:   waiveLate,
        waive_absent: waiveAbsent,
        reason:       waiveReason.trim(),
      });
      toast.success('✅ Annulation enregistrée');
      setWaiveOpen(false);
      fetchAttendances();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setWaiving(false); }
  };

  const handleBulkWaive = async () => {
    if (!bulkWaiveLate && !bulkWaiveAbsent) { toast.error('Sélectionnez au moins un type à annuler'); return; }
    if (!bulkReason.trim())                  { toast.error('Le motif est obligatoire'); return; }
    if (!window.confirm(`Annuler TOUS les retards/absences du ${bulkStart} au ${bulkEnd} ?\nCette action est irréversible.`)) return;
    setBulkWaiving(true);
    try {
      const res = await api.post('/attendance/waive-bulk', {
        start_date:   bulkStart,
        end_date:     bulkEnd,
        waive_late:   bulkWaiveLate,
        waive_absent: bulkWaiveAbsent,
        reason:       bulkReason.trim(),
      });
      toast.success(`✅ ${res.data.message}`);
      setBulkWaiveOpen(false);
      setBulkReason('');
      fetchAttendances();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setBulkWaiving(false); }
  };

  const getStatusBadge = (att) => {
    const status = att?.status;
    const justif = att?.justification_status;
    // Retard excusé
    if (att?.late_waived)   return <Badge className="bg-teal-100 text-teal-700 border border-teal-300" title={`Excusé par ${att.waived_by || 'RH'} — ${att.waiver_reason || ''}`}>Retard excusé ✓</Badge>;
    // Absence excusée
    if (att?.absent_waived) return <Badge className="bg-teal-100 text-teal-700 border border-teal-300" title={`Excusé par ${att.waived_by || 'RH'} — ${att.waiver_reason || ''}`}>Absence excusée ✓</Badge>;
    if (att?.is_absent || status === 'absent') {
      if (justif === 'approved') return <Badge className="bg-blue-500 text-white" title="Absence justifiée">Justifiée</Badge>;
      if (justif === 'pending')  return <Badge className="bg-amber-500 text-white" title="Justification en attente">Justif. en attente</Badge>;
      if (justif === 'rejected') return <Badge className="bg-red-700 text-white" title="Justification refusée">Absent (justif. refusée)</Badge>;
      return <Badge className="bg-error text-white">Absent</Badge>;
    }
    const v = {
      present: { l: 'Present', c: 'bg-success text-white' },
      late:    { l: 'Retard',  c: 'bg-warning text-white' },
      half_day:{ l: 'Demi-journee', c: 'bg-slate-500 text-white' }
    };
    const s = v[status] || v.present;
    return <Badge className={s.c}>{s.l}</Badge>;
  };

  const getShiftTypeBadge = (shiftType) => {
    const map = {
      day:       { l: 'Jour',      c: 'bg-slate-200 text-slate-800' },
      night:     { l: 'Nuit',      c: 'bg-indigo-600 text-white' },
      saturday:  { l: 'Samedi',    c: 'bg-amber-500 text-white' },
      exception: { l: 'Exception', c: 'bg-violet-600 text-white' },
    };
    const s = map[shiftType] || map.day;
    return <Badge className={s.c} data-testid={`shift-type-${shiftType || 'day'}`}>{s.l}</Badge>;
  };

  const getEmployeeName = (id) => { const e = employees.find(x => x.id === id); return e ? `${e.first_name} ${e.last_name}` : 'N/A'; };
  const getEmployeeInfo = (id, field) => { const e = employees.find(x => x.id === id); return e ? (e[field] || '-') : '-'; };

  // Filtrage par entreprise sélectionnée dans le sélecteur global
  const filteredAttendances = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return attendances;
    // Les enregistrements de présence contiennent company_id
    return attendances.filter(a => a.company_id === selectedCompanyId);
  }, [attendances, selectedCompanyId, isCrossCompany]);

  const filteredEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  // Stats (basées sur les données filtrées)
  const totalPresent = filteredAttendances.filter(a => !a.is_absent && (a.status === 'present' || a.status === 'late')).length;
  const totalLate = filteredAttendances.filter(a => a.is_late).length;
  const totalAbsent = filteredAttendances.filter(a => a.is_absent).length;
  const totalHours = filteredAttendances.reduce((sum, a) => sum + (a.hours_worked || 0), 0);

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Presences</h1>
          <p className="text-slate-600 mt-1">Suivi des presences des employes</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setAutoRefresh(v => !v)}
            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900 transition"
            data-testid="toggle-auto-refresh"
            title="Activer/désactiver le rafraîchissement automatique"
          >
            <span className={`inline-block w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
            <span className="tabular-nums">
              {autoRefresh ? 'Temps réel' : 'Pause'}
              {' • '}
              <span data-testid="last-refresh-label">
                MAJ il y a {Math.max(0, Math.floor((Date.now() - lastRefresh.getTime()) / 1000))}s
              </span>
            </span>
          </button>
          <Button variant="outline" size="sm" onClick={fetchAttendancesSilent} data-testid="manual-refresh-btn" title="Rafraîchir maintenant">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => handleExport('excel')} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
          {/* Bouton Force Majeure */}
          <Button
            variant="outline"
            onClick={() => { setBulkStart(startDate); setBulkEnd(endDate); setBulkWaiveOpen(true); }}
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid="bulk-waive-btn"
            title="Annuler tous les retards/absences sur une période (force majeure)"
          >
            <Zap className="w-4 h-4 mr-2" />
            Force Majeure
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-green-600 font-medium">Presents</p>
            <p className="text-2xl font-bold text-green-700">{totalPresent}</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-orange-600 font-medium">Retards</p>
            <p className="text-2xl font-bold text-orange-700">{totalLate}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-red-600 font-medium">Absences</p>
            <p className="text-2xl font-bold text-red-700" data-testid="stat-absent">{totalAbsent}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-blue-600 font-medium">Heures totales</p>
            <p className="text-2xl font-bold text-blue-700">{totalHours.toFixed(1)}h</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-lg">Filtres</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Employe</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="employee-filter"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {filteredEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Categorie</label>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" data-testid="category-filter">
                <option value="">Toutes</option>
                {(employeeOptions.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Poste</label>
              <select value={selectedPosition} onChange={(e) => setSelectedPosition(e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" data-testid="position-filter">
                <option value="">Tous</option>
                {(employeeOptions.positions || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Date debut</label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Date fin</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">&nbsp;</label>
              <Button onClick={handleFilter} className="w-full bg-primary" data-testid="filter-button">Filtrer</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Registre des Presences ({attendances.length} enregistrements)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold">Employe</TableHead>
                  <TableHead className="font-semibold">Cat.</TableHead>
                  <TableHead className="font-semibold">Poste</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Arrivee</TableHead>
                  <TableHead className="font-semibold">Deb. Pause</TableHead>
                  <TableHead className="font-semibold">Fin Pause</TableHead>
                  <TableHead className="font-semibold">Depart</TableHead>
                  <TableHead className="font-semibold">Heures</TableHead>
                  <TableHead className="font-semibold">Retard</TableHead>
                  <TableHead className="font-semibold">Pause</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAttendances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center text-slate-500 py-8">Aucune presence trouvee</TableCell>
                  </TableRow>
                ) : filteredAttendances.map(att => (
                  <TableRow key={att.id} data-testid={`admin-attendance-row-${att.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{att.date}</TableCell>
                    <TableCell>{getEmployeeName(att.employee_id)}</TableCell>
                    <TableCell className="text-xs">{getEmployeeInfo(att.employee_id, 'category')}</TableCell>
                    <TableCell className="text-xs">{getEmployeeInfo(att.employee_id, 'position')}</TableCell>
                    <TableCell>{getShiftTypeBadge(att.shift_type)}</TableCell>
                    <TableCell>
                      {att.check_in ? <div className="flex items-center gap-1 font-mono text-sm"><Clock className="w-3 h-3 text-green-500" />{formatPunchTime(att.check_in)}</div> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell>
                      {att.break_start ? <div className="flex items-center gap-1 font-mono text-sm"><Coffee className="w-3 h-3 text-amber-500" />{formatPunchTime(att.break_start)}</div> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell>
                      {att.break_end ? <div className="flex items-center gap-1 font-mono text-sm"><Coffee className="w-3 h-3 text-amber-500" />{formatPunchTime(att.break_end)}</div> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell>
                      {att.check_out ? <div className="flex items-center gap-1 font-mono text-sm"><Clock className="w-3 h-3 text-red-500" />{formatPunchTime(att.check_out)}</div> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="font-medium">{(att.hours_worked || 0).toFixed(2)}h</TableCell>
                    <TableCell>
                      {att.is_late ? <div className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-orange-500" /><span className="text-orange-600 text-sm font-medium">{att.late_minutes || 0} min</span></div> : <span className="text-green-600 text-sm">OK</span>}
                    </TableCell>
                    <TableCell>
                      {(att.break_duration_minutes || 0) > 0 ? <div className="text-sm"><span>{att.break_duration_minutes} min</span>{(att.break_overtime_minutes || 0) > 0 && <div className="text-red-500 text-xs font-medium">+{att.break_overtime_minutes} min</div>}</div> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell>{getStatusBadge(att)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" title="Recalculer" onClick={() => handleRecalculate(att.employee_id, att.date)} data-testid={`recalc-${att.id}`}>
                          <RefreshCw className="w-3 h-3 text-blue-500" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Corriger" onClick={() => openCorrection(att)} data-testid={`correct-${att.id}`}>
                          <Edit className="w-3 h-3 text-amber-500" />
                        </Button>
                        {/* Bouton Annuler retard/absence — visible si retard ou absence non encore excusé */}
                        {(att.is_late || att.is_absent) && !att.late_waived && !att.absent_waived && (
                          <Button size="icon" variant="ghost" title="Annuler retard / absence" onClick={() => openWaive(att)} data-testid={`waive-${att.id}`}>
                            <ShieldOff className="w-3 h-3 text-teal-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Device timezone info bar */}
      {deviceTimezones.length > 0 && (
        <Card className="border-slate-200 bg-slate-50/50" data-testid="device-timezone-bar">
          <CardContent className="py-3">
            <div className="flex items-start gap-3 text-sm">
              <Clock className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-slate-700">
                  <strong>Heures affichées dans le fuseau de chaque pointeuse</strong>
                  {' '}— l'heure visible correspond à l'heure réelle du pointage, indépendamment du fuseau de votre navigateur.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {deviceTimezones.map(d => (
                    <Badge
                      key={d.serial}
                      variant="outline"
                      className="bg-white text-slate-700 border-slate-300 gap-1.5 py-1"
                      data-testid={`tz-badge-${d.serial}`}
                      title={d.location || d.serial}
                    >
                      <span className="font-medium">{d.name || d.serial}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-600">{d.timezone}</span>
                      {getUtcOffsetLabel(d.timezone) && (
                        <span className="text-slate-500">({getUtcOffsetLabel(d.timezone)})</span>
                      )}
                      <span className="text-slate-400">·</span>
                      <span className="font-mono text-blue-700" data-testid={`tz-now-${d.serial}`}>
                        {getCurrentTimeInTz(d.timezone)}
                      </span>
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Correction Modal */}
      <Dialog open={correctionOpen} onOpenChange={setCorrectionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Correction manuelle</DialogTitle></DialogHeader>
          <form onSubmit={handleCorrection} className="space-y-4">
            <p className="text-sm text-slate-600">
              Employe: <strong>{getEmployeeName(correctionData.employee_id)}</strong> | Date: <strong>{correctionData.date}</strong>
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Arrivee</Label>
                <Input type="time" value={correctionData.check_in} onChange={(e) => setCorrectionData({ ...correctionData, check_in: e.target.value })} data-testid="correction-checkin" />
              </div>
              <div className="space-y-2">
                <Label>Depart</Label>
                <Input type="time" value={correctionData.check_out} onChange={(e) => setCorrectionData({ ...correctionData, check_out: e.target.value })} data-testid="correction-checkout" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Deb. Pause</Label>
                <Input type="time" value={correctionData.break_start} onChange={(e) => setCorrectionData({ ...correctionData, break_start: e.target.value })} data-testid="correction-break-start" />
              </div>
              <div className="space-y-2">
                <Label>Fin Pause</Label>
                <Input type="time" value={correctionData.break_end} onChange={(e) => setCorrectionData({ ...correctionData, break_end: e.target.value })} data-testid="correction-break-end" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motif de correction *</Label>
              <Textarea value={correctionData.reason} onChange={(e) => setCorrectionData({ ...correctionData, reason: e.target.value })} required placeholder="Raison de la correction..." rows={2} data-testid="correction-reason" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setCorrectionOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="correction-submit">Appliquer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Modale Annulation retard / absence ── */}
      <Dialog open={waiveOpen} onOpenChange={setWaiveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="w-5 h-5 text-teal-600" />
              Annuler un retard / une absence
            </DialogTitle>
          </DialogHeader>

          {waiveTarget && (
            <div className="space-y-4 pt-2">
              {/* Infos de l'enregistrement */}
              <div className="rounded-md bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
                <p className="font-semibold text-slate-700">{waiveTarget.employee_name || waiveTarget.employee_id}</p>
                <p className="text-slate-500">Date : <strong>{waiveTarget.date}</strong></p>
                {waiveTarget.is_late   && <p className="text-orange-600">⚠ Retard de {waiveTarget.late_minutes || 0} min</p>}
                {waiveTarget.is_absent && <p className="text-red-600">✗ Absence</p>}
              </div>

              {/* Checkboxes */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Que souhaitez-vous annuler ?</Label>
                {waiveTarget.is_late && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={waiveLate}
                      onChange={e => setWaiveLate(e.target.checked)}
                      className="w-4 h-4 accent-teal-600"
                      data-testid="waive-late-cb"
                    />
                    <span className="text-sm text-orange-700 font-medium">Annuler le retard ({waiveTarget.late_minutes || 0} min)</span>
                  </label>
                )}
                {waiveTarget.is_absent && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={waiveAbsent}
                      onChange={e => setWaiveAbsent(e.target.checked)}
                      className="w-4 h-4 accent-teal-600"
                      data-testid="waive-absent-cb"
                    />
                    <span className="text-sm text-red-700 font-medium">Annuler l'absence (marquer comme présent)</span>
                  </label>
                )}
              </div>

              {/* Motif */}
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-slate-700">Motif <span className="text-red-500">*</span></Label>
                <Textarea
                  value={waiveReason}
                  onChange={e => setWaiveReason(e.target.value)}
                  placeholder="Ex : Autorisation préalable du directeur, justificatif reçu..."
                  rows={3}
                  data-testid="waive-reason"
                />
                <p className="text-xs text-slate-400">Ce motif sera enregistré et visible dans l'historique.</p>
              </div>

              {/* Boutons */}
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setWaiveOpen(false)} className="flex-1" disabled={waiving}>
                  Annuler
                </Button>
                <Button
                  onClick={handleWaive}
                  disabled={waiving || (!waiveLate && !waiveAbsent) || !waiveReason.trim()}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="waive-submit"
                >
                  {waiving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : '✓ Confirmer l\'annulation'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modale Force Majeure (annulation en masse) ── */}
      <Dialog open={bulkWaiveOpen} onOpenChange={setBulkWaiveOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <Zap className="w-5 h-5" />
              Annulation en masse — Force Majeure
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="rounded-md bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800">
              ⚠️ Cette action annule <strong>tous</strong> les retards et/ou absences sur la période sélectionnée pour <strong>tous les employés</strong>. Utilisez uniquement pour des cas de force majeure.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm font-semibold">Du <span className="text-red-500">*</span></Label>
                <Input type="date" value={bulkStart} onChange={e => setBulkStart(e.target.value)} data-testid="bulk-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-semibold">Au <span className="text-red-500">*</span></Label>
                <Input type="date" value={bulkEnd} onChange={e => setBulkEnd(e.target.value)} data-testid="bulk-end" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Annuler :</Label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bulkWaiveLate} onChange={e => setBulkWaiveLate(e.target.checked)} className="w-4 h-4 accent-orange-600" data-testid="bulk-late-cb" />
                <span className="text-sm text-orange-700 font-medium">Tous les retards</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bulkWaiveAbsent} onChange={e => setBulkWaiveAbsent(e.target.checked)} className="w-4 h-4 accent-orange-600" data-testid="bulk-absent-cb" />
                <span className="text-sm text-red-700 font-medium">Toutes les absences</span>
              </label>
            </div>
            <div className="space-y-1">
              <Label className="text-sm font-semibold">Motif <span className="text-red-500">*</span></Label>
              <Textarea
                value={bulkReason}
                onChange={e => setBulkReason(e.target.value)}
                placeholder="Ex : Grève générale du 21/10/2026, inondations, panne électrique..."
                rows={3}
                data-testid="bulk-reason"
              />
              <p className="text-xs text-slate-400">Ce motif sera appliqué à tous les enregistrements modifiés.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setBulkWaiveOpen(false)} className="flex-1" disabled={bulkWaiving}>Annuler</Button>
              <Button
                onClick={handleBulkWaive}
                disabled={bulkWaiving || (!bulkWaiveLate && !bulkWaiveAbsent) || !bulkReason.trim() || !bulkStart || !bulkEnd}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="bulk-waive-submit"
              >
                {bulkWaiving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Application...</> : <><Zap className="w-4 h-4 mr-2" />Appliquer à tous</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
